import React, { useState } from 'react';
import { Cheque as ChequeType } from '@baemingo/table-events/dist/types';

import { addToast } from '~/actions';
import { useTableApi, useTableUniverse } from '~Components/TableUniverse';
import { BaseButton, SidebarTitle, SidebarValue } from '~Scenes/Handy/styles';
import Cheque from '../Cheque';
import {
  useButton,
  useCurrency,
  useDeviceInfo,
  useDispatch,
  useI18n,
  useKitchenMessage,
  useLicenses,
  useLogger,
  useSelector,
  useStaffCan,
  useWaste,
} from '~Hooks';
import NewCheque from '../NewCheque';
import { useTableContext } from '~Scenes/Handy/Context/TableContext';
import { useChequeContext } from '~Scenes/Handy/Context/ChequeContext';
import DragTargets from './DragTargets';
import { useStaffContext } from '~Scenes/Handy/Context/StaffProvider';
import Modal from '~ComponentsImproved/Modal';
import ChequeMessageButton from './ChequeMessageButton';
import { Message } from '~Components/PredefinedMessages';
import PrintButton from './PrintButton';
import { DraggedCheque } from './DeleteChequeTarget';
import { useReportsApi } from '~Components/ReportsUniverse';
import { useStorage } from '~Components/Storage';
import { useCompetition, SUBTRACT_SALE_TYPE } from '~Providers/CompetitionsProvider';
import { useCashDrawer } from '~Providers/CashDrawerProvider';
import SelectOptionModal from '~Components/SelectOptionModal';
import { range } from 'lodash';
import styled from '@emotion/styled';
import { DateTime } from 'luxon';
import RemoveItems from '~Components/RemoveItems';
import EditChequeActions from './EditChequeActions';
import { useInventoryApi, useInventoryUniverse } from '~Components/InventoryUniverse';
import { BlueButton } from '~Styles';

const CreateChequeButton = styled(BaseButton)`
  padding: 32px 4px;
  background: var(--colorGradientGreen);
`;

const OpenPOSButton = styled(BlueButton)`
  background: var(--colorGradientBlue);
  width: 64px;
  margin: 0 8px 8px;
  font-size: 12px;
`;

const GoToTableButton = styled(BaseButton)`
  padding: 32px 4px;
  background: var(--colorBlueGray50);
  color: var(--colorBlueGray1300);
`;

const Wrapper = styled.div`
  flex: 0 0 80px;
  height: 100%;
  background: #545960;
  display: flex;
  flex-direction: column;
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const PanelSection = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
`;

const PanelTop = styled(PanelSection)`
  justify-content: flex-start;
  padding: 8px;
  flex: 1;

  & > *:not(:last-child) {
    margin-bottom: 8px;
  }
`;

const PanelBottom = styled(PanelSection)<{
  isPhone: boolean;
}>`
  justify-content: flex-end;
  padding-bottom: ${({ isPhone }) => (isPhone ? '24px' : '0')};
  flex: 0;
  & > div {
    margin: 0 8px 8px;
  }
`;

const TableLabel = styled.b`
  display: flex;
  justify-content: center;
  font-size: 14px;
  margin-bottom: 4px !important;
`;

interface Props {
  cheques: ChequeType[];
  onCreateCheque: (coverage: number) => void;
  onGoToTable: () => void;
  onSelectCheque: (cheque: ChequeType) => void;
  onOrder: () => void;
  onMergeCheque: (chequeId: string, targetId: string) => void;
  goToTables?: () => void;
  chequeIsDragging: boolean;
  setChequeIsDragging: (value: boolean) => void;
  messagesList?: (Message | string)[];
  isQSR?: boolean;
}

const ChequePanel: React.FC<Props> = ({
  cheques,
  onCreateCheque,
  onSelectCheque,
  onGoToTable,
  onOrder,
  onMergeCheque,
  goToTables,
  chequeIsDragging,
  setChequeIsDragging,
  messagesList,
  isQSR,
}: Props) => {
  const [storage] = useStorage();
  const cashDrawer = useCashDrawer();
  const licenses = useLicenses();
  const { i18n } = useI18n();
  const currency = useCurrency();
  const universe = useTableUniverse();
  const dispatch = useDispatch();
  const { authedStaff } = useStaffContext();
  const { selectedTable } = useTableContext();
  const { selectedCheque, setSelectedCheque } = useChequeContext();
  const logger = useLogger('handy');
  const tableAPI = useTableApi();
  const reportsApi = useReportsApi();
  const competitions = useCompetition();
  const staffCan = useStaffCan();
  const waste = useWaste();
  const sendKitchenMessage = useKitchenMessage();
  const [showCoverageModal, setShowCoverageModal] = useState<boolean>(false);
  const venueId = useSelector((state) => state.venueId) as string;
  const deviceWakeState = useSelector((state) => state.deviceWakeState);

  const inventoryUniverse = useInventoryUniverse();
  const inventoryApi = useInventoryApi(venueId);

  const goToTableButton = useButton({
    onClick: onGoToTable,
  });

  const handleOpenPOS = () => {
    if (cashDrawer.isOpen) {
      dispatch(addToast(i18n('Cash.NotAllowed'), true));
      return;
    }

    onOrder();
  };

  const chequesOnTable = cheques.filter((c) => c.tableId === selectedTable?.id);

  const universeSelectedCheque = universe.cheques.find((c) => c.id === selectedCheque?.id);

  const selectedChequeHasPayment = universeSelectedCheque && universeSelectedCheque.paidAmount > 0;
  // When not on the tables view the first cheque is always the one that will be "selected"
  const firstChequeHasPayment = chequesOnTable[0]?.paidAmount > 0;

  const openPOSButton = useButton({
    onClick: handleOpenPOS,
    disabled: Boolean(selectedChequeHasPayment) || (!selectedCheque && firstChequeHasPayment),
    disabledToast: i18n('TOAST.DisableOrderingAfterPayment'),
  });

  const createChequeButton = useButton({
    onClick: () => {
      if (!storage.hideCoverageModal) {
        setShowCoverageModal(true);
      } else {
        onCreateCheque(0);
      }
    },
    disabled: deviceWakeState.syncingTableEvents,
    disabledToast: i18n('Handy.Tables.LoadingEvents'),
  });

  // This state temporarily holds the chequeId while user inputs delete message
  const [isDeletingChequeId, setIsDeletingChequeId] = useState<string | null>(null);

  const deviceInfo = useDeviceInfo();

  const chequeItems = universe.items.filter((ci) => {
    return cheques.some((c) => ci.chequeId === c.id);
  });

  const totalForSelectedTable = cheques.reduce((acc, cheque) => {
    return acc + cheque.amount - cheque.discount;
  }, 0);

  const prettySeatedTime = () => {
    const now = DateTime.now();
    const createdAt = DateTime.fromISO(cheques[0].createdAt);
    const seatedMinutes = now.diff(createdAt, 'minutes').minutes;

    if (seatedMinutes < 60) {
      return Math.floor(seatedMinutes) + ' min';
    }
    if (seatedMinutes > 540) {
      return '> 9 h';
    }
    return `${Math.floor(now.diff(createdAt, 'hours').hours)} hours`;
  };

  const handleDeleteItemsThenCheque = (
    chequeId: string,
    message: string,
    commentOnWaste?: string,
  ) => {
    if (!chequeId || !message) {
      return;
    }

    const cheque = cheques.find((c) => c.id === chequeId);

    // Find all items on the cheque
    const itemsOnChequeForDeletion = chequeItems.filter((item) => item.chequeId === chequeId);
    const itemsTotalAmount = itemsOnChequeForDeletion.reduce(
      (acc, item) => acc + (item.amount - item.discount),
      0,
    );

    tableAPI.removeItems(
      itemsOnChequeForDeletion,
      chequeId,
      message,
      cheque?.tableId || '',
      Boolean(commentOnWaste),
    );

    if (commentOnWaste !== undefined) {
      const itemsToWaste = itemsOnChequeForDeletion.map((item) => ({
        itemId: item.itemId,
        fraction: item.fraction,
        title: item.title,
        message: item.message,
      }));
      waste.register(itemsToWaste, message, commentOnWaste);
    } else {
      /** Correction - add back inventory */
      itemsOnChequeForDeletion.forEach((item) => {
        const invItem = inventoryUniverse.items.find((i) => i.itemId === item.itemId);

        if (invItem && typeof invItem.count === 'number') {
          inventoryApi.addStock(invItem.itemId, item.fraction);
        }
      });
    }

    reportsApi.orderLineCorrected('REMOVE_CHEQUE', itemsTotalAmount);

    // We subtract from the owner of the cheque, because a common scenario IRL is that the head waiter removes items from a cheque
    competitions.subtractSale({
      staffId: cheque?.owner,
      items: itemsOnChequeForDeletion,
      type: SUBTRACT_SALE_TYPE.REMOVE_CHEQUE,
    });

    if (storage.printVoidedItems) {
      const voidItems = itemsOnChequeForDeletion.map((item) => {
        return {
          itemId: item.itemId,
          categoryId: item.categoryId,
          title: item.title,
          fraction: item.fraction,
          price: item.amount - item.discount,
        };
      });

      sendKitchenMessage({
        type: 'VOID_CHEQUE',
        tableName: selectedTable?.code ?? '',
        staffName: authedStaff?.name ?? '',
        items: voidItems,
        message: message,
      });
    }

    handleDeleteCheque(chequeId, message);
  };

  const handleDeleteCheque = (chequeId: string, message?: string) => {
    if (!chequeId) {
      return;
    }

    const itemsOnChequeForDeletion = chequeItems.filter((item) => item.chequeId === chequeId);

    // If the cheque has items, show reason modal and cancel deletion
    if (itemsOnChequeForDeletion.length > 0 && !message) {
      setIsDeletingChequeId(chequeId);
      return;
    }

    logger.info('Staff deleted cheque', {
      chequeId: chequeId,
      table: selectedTable,
      staff: authedStaff,
    });

    tableAPI.removeCheque(chequeId, selectedTable?.id || '');

    const filteredCheques = cheques.filter((c) => c.id !== chequeId);

    const newSelectedCheque = filteredCheques[0] ?? null;
    setSelectedCheque(newSelectedCheque);

    // Show toast
    dispatch(addToast(i18n('Handy.Cheque.Deleted'), true));
  };

  const TableInfo = () => (
    <div>
      <SidebarValue>{prettySeatedTime()}</SidebarValue>

      {!storage.hideCoverageModal && (
        <>
          <SidebarTitle>{i18n('Handy.Tables.ChequePanel.Coverage')}</SidebarTitle>
          <SidebarValue>{chequesOnTable[0].coverage ?? 0}</SidebarValue>
        </>
      )}

      <SidebarTitle>{i18n('Handy.Tables.ChequePanel.Total')}</SidebarTitle>
      <SidebarValue>{currency.format(totalForSelectedTable)}</SidebarValue>

      <SidebarTitle>{i18n('Handy.Tables.ChequePanel.Items')}</SidebarTitle>
      <SidebarValue>{chequeItems.length}</SidebarValue>
    </div>
  );

  const hasOrderingLicense = licenses.hasHandheldOrder || licenses.hasHandheldPro;

  /**
   * Here we check if the staff has permission to order on the selected cheque (when the cheque is selected)
   * Or, if no cheque is selected, check the first cheque on the table that the staff can mutate (when in tables view)
   * This is because we look at the first cheque on actions if there is no selected cheque
   */
  const staffCanMutateGivenCheque = staffCan.mutateCheque(
    selectedCheque?.id ?? chequesOnTable[0]?.id ?? '',
  );

  return (
    <Wrapper>
      <PanelTop>
        {selectedTable?.code && (
          <TableLabel>
            {i18n('Handy.Table')} {selectedTable?.code}
          </TableLabel>
        )}
        {!isQSR && !!cheques.length && !selectedCheque && <TableInfo />}
        {selectedTable &&
          cheques.map((cheque) => (
            <Cheque
              selected={selectedCheque?.id === cheque.id}
              data-testid="cheque"
              key={cheque.id}
              cheque={cheque}
              isOnTablesView={!selectedCheque}
              onSelect={onSelectCheque}
              onMerge={onMergeCheque}
              setIsDragging={setChequeIsDragging}
            />
          ))}

        {hasOrderingLicense && ((!isQSR && selectedCheque) || isQSR) && selectedTable && (
          <NewCheque
            enableShowCheckAnimation={() => {}}
            selectCheque={() => {}}
            showBong={() => {
              /* noop */
            }}
            showLogin={() => {
              /* noop */
            }}
            nrOfCheques={cheques.length}
            tableCheques={cheques}
            disableCoverage={isQSR || storage.hideCoverageModal}
          />
        )}
      </PanelTop>
      <PanelBottom isPhone={Boolean(deviceInfo?.isPhone)}>
        {messagesList &&
          hasOrderingLicense &&
          selectedTable &&
          !selectedCheque &&
          staffCanMutateGivenCheque &&
          cheques.length > 0 && <ChequeMessageButton messagesList={messagesList} />}

        {hasOrderingLicense && !cheques.length && selectedTable && !isQSR && (
          <CreateChequeButton data-testid="add-cheque" {...createChequeButton}>
            {i18n('Handy.Table.Open')}
          </CreateChequeButton>
        )}
        {!chequeIsDragging && selectedTable && selectedCheque && !!cheques.length && (
          <EditChequeActions />
        )}
        {!chequeIsDragging && selectedTable && !!cheques.length && staffCanMutateGivenCheque && (
          <PrintButton />
        )}
        {!!cheques.length && !selectedCheque && (
          <>
            <GoToTableButton data-testid="go-to-table" {...goToTableButton}>
              {i18n('Handy.Tables.GoToTable')}
            </GoToTableButton>
          </>
        )}
        {!chequeIsDragging &&
          hasOrderingLicense &&
          selectedTable &&
          staffCanMutateGivenCheque &&
          ((cheques.length > 0 && !isQSR) || selectedCheque) && (
            <OpenPOSButton {...openPOSButton} size="large">
              {i18n('Handy.Order')}
            </OpenPOSButton>
          )}

        {isDeletingChequeId && (
          <Modal isShowing={true} hide={() => setIsDeletingChequeId(null)}>
            <RemoveItems
              onComplete={(message, commentOnWaste) => {
                setIsDeletingChequeId(null);
                handleDeleteItemsThenCheque(isDeletingChequeId, message, commentOnWaste);
              }}
              chequeId={isDeletingChequeId}
              itemIdsToRemove={universe.items
                .filter((i) => i.chequeId === isDeletingChequeId)
                .map((i) => i.id)}
            />
          </Modal>
        )}

        {chequeIsDragging && selectedCheque && selectedTable && (
          <DragTargets
            goToTables={goToTables}
            onDeleteCheque={(dropData: DraggedCheque) => {
              handleDeleteCheque(dropData.id);
            }}
          />
        )}
      </PanelBottom>

      {showCoverageModal && (
        <SelectOptionModal
          enableCustomInput
          title={i18n('TM.Coverage')}
          options={range(1, 8)}
          onSelect={(count) => {
            onCreateCheque(count as number);
            setShowCoverageModal(false);
          }}
        />
      )}
    </Wrapper>
  );
};

export default ChequePanel;
